import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Img } from "@/components/ui/img";
import { cn } from "@/lib/utils";
import { stripBosnian } from "@/lib/languages";
import { CaretRight } from "@phosphor-icons/react";
import { THERAPIST_STATUS_CONFIG_SINGULAR } from "@/lib/status-configs";
import { getInitials } from "@/components/admin/payouts/payout-helpers";
import { parseSpecializations } from "./helpers";
import type { Therapist } from "@/types";

function formatDateNumeric(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}.`;
}

function detailPath(therapist: Therapist): string {
  return `/dashboard/admin/therapists/${therapist.id}`;
}

/* ─── Shared pieces ─── */

function TherapistAvatar({
  therapist,
  className,
}: {
  therapist: Therapist;
  className?: string;
}) {
  if (therapist.profileImageUrl) {
    return (
      <Img
        src={therapist.profileImageUrl}
        alt={therapist.userName}
        className={cn("shrink-0 rounded-full object-cover", className)}
      />
    );
  }
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-bold text-primary",
        className,
      )}
    >
      {getInitials(therapist.userName)}
    </div>
  );
}

function StatusBadge({ therapist }: { therapist: Therapist }) {
  if (therapist.status === "approved" && therapist.deactivatedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground ring-1 ring-border">
        <span className="size-1.5 rounded-full bg-muted-foreground/50" />
        Deaktiviran
      </span>
    );
  }
  const config = THERAPIST_STATUS_CONFIG_SINGULAR[therapist.status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1",
        config.bg,
        config.color,
        config.ring,
      )}
    >
      <span className={cn("size-1.5 rounded-full", config.dot)} />
      {therapist.status === "approved" ? "Aktivan" : config.label}
    </span>
  );
}

function SpecializationPills({
  therapist,
  max,
}: {
  therapist: Therapist;
  max: number;
}) {
  const specializations = parseSpecializations(therapist.specializations);
  if (specializations.length === 0) {
    return <span className="text-xs text-muted-foreground/60">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {specializations.slice(0, max).map((s) => (
        <span
          key={s}
          className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
        >
          {s}
        </span>
      ))}
      {specializations.length > max && (
        <span
          className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
          title={specializations.slice(max).join(", ")}
        >
          +{specializations.length - max}
        </span>
      )}
    </div>
  );
}

/** Single-line muted text version for the desktop table; full list in tooltip. */
function SpecializationLine({ therapist }: { therapist: Therapist }) {
  const specializations = parseSpecializations(therapist.specializations);
  if (specializations.length === 0) {
    return <span className="text-muted-foreground/50">—</span>;
  }
  const shown = specializations.slice(0, 2).join(" · ");
  const rest = specializations.length - 2;
  return (
    <span
      className="block truncate text-muted-foreground"
      title={specializations.join(", ")}
    >
      {shown}
      {rest > 0 && <span className="text-muted-foreground/60"> +{rest}</span>}
    </span>
  );
}

function LanguageBadges({ therapist }: { therapist: Therapist }) {
  const { t } = useTranslation();
  const extraLangs = stripBosnian(therapist.languages);
  if (!therapist.isVolunteer && extraLangs.length === 0) return null;
  return (
    <>
      {therapist.isVolunteer && (
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
          Volonter
        </span>
      )}
      {extraLangs.slice(0, 2).map((code) => (
        <span
          key={code}
          className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-800"
        >
          {t(`languages.names.${code}`)}
        </span>
      ))}
      {extraLangs.length > 2 && (
        <span
          className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-800"
          title={extraLangs
            .slice(2)
            .map((code) => t(`languages.names.${code}`))
            .join(", ")}
        >
          +{extraLangs.length - 2}
        </span>
      )}
    </>
  );
}

/* ─── Desktop table + mobile cards ─── */

export function TherapistsTable({ therapists }: { therapists: Therapist[] }) {
  const navigate = useNavigate();

  return (
    <>
      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm lg:block">
        <table className="w-full table-fixed text-[13px]">
          <colgroup>
            <col className="w-[280px]" />
            <col />
            <col className="w-[108px]" />
            <col className="w-[128px]" />
            <col className="w-[56px]" />
          </colgroup>
          <thead>
            <tr className="bg-muted/40">
              <Th>Terapeut</Th>
              <Th>Specijalizacije</Th>
              <Th align="right">Prijava</Th>
              <Th align="right">Status</Th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {therapists.map((therapist, i) => (
              <tr
                key={therapist.id}
                onClick={() => navigate({ to: detailPath(therapist) })}
                className={cn(
                  "group cursor-pointer transition-colors hover:bg-primary/[0.03]",
                  i !== therapists.length - 1 && "border-b border-border/40",
                )}
              >
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <TherapistAvatar
                      therapist={therapist}
                      className="size-9 text-[11px]"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium leading-tight text-foreground">
                          {therapist.userName}
                        </span>
                        {therapist.isVolunteer && (
                          <span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-px text-[10px] font-medium text-emerald-800">
                            Volonter
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                        {therapist.userEmail}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <SpecializationLine therapist={therapist} />
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums text-muted-foreground">
                  {formatDateNumeric(therapist.createdAt)}
                </td>
                <td className="px-3 py-3.5 text-right">
                  <StatusBadge therapist={therapist} />
                </td>
                <td className="py-3.5 pl-1 pr-4 text-right">
                  <Link
                    to={detailPath(therapist)}
                    aria-label={`Detalji: ${therapist.userName}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex size-7 items-center justify-center rounded-lg text-muted-foreground/50 transition-colors group-hover:text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <CaretRight className="size-4" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
        {therapists.map((therapist) => (
          <Link
            key={therapist.id}
            to={detailPath(therapist)}
            className="group rounded-2xl border border-border/60 bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <TherapistAvatar
                  therapist={therapist}
                  className="size-11 text-xs"
                />
                <div className="min-w-0">
                  <div
                    className="truncate text-sm font-semibold text-foreground"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {therapist.userName}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {therapist.userEmail}
                  </div>
                </div>
              </div>
              <StatusBadge therapist={therapist} />
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              <LanguageBadges therapist={therapist} />
              <SpecializationPills therapist={therapist} max={2} />
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-border/40 pt-3 text-xs text-muted-foreground">
              <span className="tabular-nums">
                Prijava: {formatDateNumeric(therapist.createdAt)}
              </span>
              <CaretRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={cn(
        "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground first:px-4",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  );
}
